import { ArrowRight, Info } from 'lucide-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppContext } from '@/app/providers/AppContextProvider';
import { Button } from '@/components/ui/button';
import apiService from '@/services/apiService';
import { notifyParent } from '@/services/parentMessenger';
import Title from '../Title';

interface StepProps {
	step: number;
	isSaving: boolean;
	setIsSaving: (saving: boolean) => void;
}

function Step5({ step }: StepProps): React.JSX.Element | null {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const { applicationData } = useAppContext();
	const [activationUrl, setActivationUrl] = useState<string | null>(null);
	const [isPolling, setIsPolling] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [showAlternateMessage, setShowAlternateMessage] = useState(false);
	const [pollingTimeout, setPollingTimeout] = useState(false);
	const poller = useRef<ReturnType<typeof setTimeout> | null>(null);
	const conversionNotifiedRef = useRef(false);

	// Lead-tracking: tell the iframe parent once the user has effectively
	// converted — i.e. the activation URL is in hand. Fires at most once per
	// mount; no-op when not iframed or no parent origin was captured.
	useEffect(() => {
		if (!activationUrl || conversionNotifiedRef.current) return;
		conversionNotifiedRef.current = true;
		notifyParent('conversion');
	}, [activationUrl]);

	// Effect for alternating messages every 3 seconds
	useEffect(() => {
		if (step !== 5) return;

		const interval = setInterval(() => {
			setShowAlternateMessage((prev) => !prev);
		}, 2500);

		return () => clearInterval(interval);
	}, [step]);

	useEffect(() => {
		if (step !== 5 || !id || !applicationData) return;

		let isActive = true;
		let attempts = 0;
		const maxAttempts = 150;
		const pollingInterval = 2000;
		const baseDelay = 2000;
		const timeoutDelay = 10000;
		let delayTimer: ReturnType<typeof setTimeout> | null = null;

		const timeoutId = setTimeout(() => {
			if (isActive && !activationUrl) {
				setPollingTimeout(true);
				setIsPolling(false);
			}
		}, timeoutDelay);

		const startPolling = () => {
			if (!isActive) return;

			if (attempts >= maxAttempts) {
				if (isActive) {
					setPollingTimeout(true);
					setIsPolling(false);
				}
				return;
			}

			setIsPolling(true);

			delayTimer = setTimeout(async () => {
				if (!isActive) return;

				try {
					const data = await apiService.activation.fetch(id);
					if (!isActive) return;

					if (data?.activation_redirect_url) {
						setActivationUrl(data.activation_redirect_url);
						setIsPolling(false);
						setPollingTimeout(false);
						return;
					}

					attempts += 1;
					poller.current = setTimeout(startPolling, pollingInterval);
				} catch (err) {
					if (!isActive) return;
					console.error('Error polling for activation URL:', err);
					setError(t('application.steps.step7.error'));
					setIsPolling(false);
				}
			}, baseDelay);
		};

		startPolling();

		return () => {
			isActive = false;
			if (delayTimer) clearTimeout(delayTimer);
			if (poller.current) clearTimeout(poller.current);
			clearTimeout(timeoutId);
		};
	}, [step, id, applicationData, activationUrl, t]);

	const handleActivationClick = () => {
		if (!activationUrl || !/^https?:\/\//i.test(activationUrl)) return;
		window.open(activationUrl, '_blank', 'noopener,noreferrer');
	};

	if (step !== 5) return null;

	return (
		<div className="flex flex-col justify-center">
			<Title title={t('application.steps.step7.title')} />
			<p className="text-xl text-center mb-4">
				<Trans
					i18nKey="application.steps.step7.createAccount"
					components={{ strong: <strong /> }}
				/>
			</p>
			<p className="text-xl text-center mb-12">{t('application.steps.step7.manageAccount')}</p>

			{/* Polling for activation url - Showing message that activation is in progress */}
			{isPolling && !pollingTimeout && !activationUrl && (
				<div className="text-center">
					<h5 className="text-center text-primary mb-10 leading-relaxed">
						{showAlternateMessage ? (
							t('application.steps.step7.processing1')
						) : (
							<Trans
								i18nKey="application.steps.step7.processing2"
								components={{ strong: <strong /> }}
							/>
						)}
					</h5>
					<div className="text-center mt-8">
						<Button
							type="button"
							variant="default"
							size="lg"
							className="px-5 py-3 text-base font-bold"
							disabled
							loading={true}
							iconRight={<ArrowRight className="w-4 h-4" />}
						>
							{t('application.steps.step7.activateButton')}
						</Button>
					</div>
				</div>
			)}

			{/* If activation url was found, Show message that account creation was successful and show the button that opens the activation url in a new tab */}
			{activationUrl && (
				<div className="text-center">
					<h5 className="text-2xl font-bold text-center text-primary mb-7 leading-relaxed">
						{t('application.steps.step7.successTitle')}
					</h5>
					<p className="text-center mb-7">
						<Trans
							i18nKey="application.steps.step7.successDescription"
							components={{ strong: <strong /> }}
						/>
					</p>
					<div className="text-center mt-8">
						<Button
							type="button"
							variant="default"
							size="lg"
							className="px-5 py-3 text-base font-bold"
							onClick={handleActivationClick}
							iconRight={<ArrowRight className="w-4 h-4" />}
							data-testid="activation-button"
						>
							{t('application.steps.step7.activateButton')}
						</Button>
					</div>
				</div>
			)}

			{/* If activation url is still null after 10 seconds of polling, Show message that account creation is not possible at this time and that the activation url was mailed to the user for later use */}
			{pollingTimeout && !activationUrl && (
				<div className="text-center" data-testid="notice-activation-failed-email-sent">
					<h5 className="text-center text-primary mb-10 leading-relaxed">
						{t('application.steps.step7.timeoutTitle')}
					</h5>
					<p className="text-center mb-7">{t('application.steps.step7.timeoutDescription')}</p>
					<div
						className="py-8 px-4 rounded-md bg-blue-50 text-blue-800 border border-blue-200 inline-flex items-center gap-2"
						data-testid="alert-activation-failed-email-sent"
					>
						<Info className="w-5 h-5" />
						{t('application.steps.step7.checkEmail')}
					</div>
				</div>
			)}

			{error && (
				<div className="mt-6 py-6 px-4 rounded-md bg-destructive/10 text-destructive border border-destructive/20">
					{error}
				</div>
			)}
		</div>
	);
}

export default Step5;
